import getWebMessengerConfig from '../integration/genesys.api';
import {
  GET_DOMAIN,
  LAUNCHER_HTMLFILE_PATH,
  LAUNCHER_CSSFILE_PATH,
  LAUNCHER_CONSUMER_SALES_CSSFILE_PATH,
  LAUNCHER_CONSUMER_SERVICE_CSSFILE_PATH,
  LAUNCHER_SMB_SALES_CSSFILE_PATH,
} from '../util/constants';
import dayLightSavingsData from '../json_files/daylightsavingdata.json';
import {
  isDSTCheck,
  getPathName,
  timeToSeconds,
  getCurrentDate,
  loadHtmlPage,
} from '../service/genesys.service';
import { getGalaxionBrand } from '../util/app.util';

const domainUrl = GET_DOMAIN();
const genesysScript = document.createElement('script');
const launcher = document.createElement('div');
const genesysCsslink = document.createElement('link');
const genesysScriptCsslink = document.createElement('link');
genesysCsslink.href = `${domainUrl}${LAUNCHER_CSSFILE_PATH}`;
genesysCsslink.type = 'text/css';
genesysCsslink.rel = 'stylesheet';
genesysScriptCsslink.type = 'text/css';
genesysScriptCsslink.rel = 'stylesheet';
const BRAND = getGalaxionBrand();

const WEB_MESSENGER_CONFIG_STORAGE_KEY = 'webMessengerConfig';
const DEFAULT_CACHE_MINUTES = 60;

const getCachedWebMessengerConfig = () => {
  const storedValue = localStorage.getItem(WEB_MESSENGER_CONFIG_STORAGE_KEY);

  if (storedValue) {
    try {
      const parsedValue = JSON.parse(storedValue);
      const expiresAt = Number(parsedValue?.expiresAt);
      const cachedBrand = parsedValue?.brand;

      if (!Number.isFinite(expiresAt) || Date.now() >= expiresAt || cachedBrand !== BRAND) {
        localStorage.removeItem(WEB_MESSENGER_CONFIG_STORAGE_KEY);
        return null;
      }

      return parsedValue.value ?? null;
    } catch (error) {
      localStorage.removeItem(WEB_MESSENGER_CONFIG_STORAGE_KEY);
      console.warn('Invalid cached web messenger config storage. Falling back to API.');
      return null;
    }
  }

  return null;
};

const setCachedWebMessengerConfig = (value, minutesToExpire, brand) => {
  const expiresAt = Date.now() + minutesToExpire * 60 * 1000;

  try {
    localStorage.setItem(
      WEB_MESSENGER_CONFIG_STORAGE_KEY,
      JSON.stringify({ value, expiresAt, brand }),
    );
  } catch (error) {
    console.warn('Failed to cache web messenger config in localStorage.');
  }
};

const getCacheMinutes = response => {
  const cacheMinutes = Number(response?.webMessengerConfig?.cache);
  return Number.isFinite(cacheMinutes) && cacheMinutes > 0 ? cacheMinutes : DEFAULT_CACHE_MINUTES;
};

const injectGenesysAssets = scriptList => {
  try {
    if (scriptList?.length > 1) {
      // getting array values from array
      const scriptArray = scriptList.values();
      const scripT = scriptArray.next().value;
      scriptList?.forEach(script => {
        const genesysscript = document.createElement('script');
        genesysscript.src = `${domainUrl}${script}`;
        document.getElementsByTagName('head')[0].appendChild(genesysscript);
        launcher.innerHTML = loadHtmlPage(`${domainUrl}${LAUNCHER_HTMLFILE_PATH}`);
        document.getElementsByTagName('body')[0].appendChild(launcher);
        document.getElementsByTagName('body')[0].appendChild(genesysCsslink);
        if (scripT.includes('genesys-chat-eir-consumer-sales')) {
          genesysScriptCsslink.href = `${domainUrl}${LAUNCHER_CONSUMER_SALES_CSSFILE_PATH}`;
          document.getElementsByTagName('body')[0].appendChild(genesysScriptCsslink);
        }
      });
    } else if (scriptList?.length === 1) {
      // getting array values from array
      const scriptArray = scriptList.values();
      const script = scriptArray.next().value;

      // Append scripts
      genesysScript.src = `${domainUrl}${scriptList}`;
      document.getElementsByTagName('head')[0].appendChild(genesysScript);

      // Append common CSS file
      document.getElementsByTagName('body')[0].appendChild(genesysCsslink);

      // Append CSS files depending on scripts
      if (script.includes('genesys-chat-eir-consumer-service')) {
        // Append HTML & CSS file
        launcher.innerHTML = loadHtmlPage(`${domainUrl}${LAUNCHER_HTMLFILE_PATH}`);
        document.getElementsByTagName('body')[0].appendChild(launcher);
        genesysScriptCsslink.href = `${domainUrl}${LAUNCHER_CONSUMER_SERVICE_CSSFILE_PATH}`;
        document.getElementsByTagName('body')[0].appendChild(genesysScriptCsslink);
      } else if (script.includes('genesys-chat-eir-smb-sales')) {
        // Append HTML & CSS file
        launcher.innerHTML = loadHtmlPage(`${domainUrl}${LAUNCHER_HTMLFILE_PATH}`);
        document.getElementsByTagName('body')[0].appendChild(launcher);
        genesysScriptCsslink.href = `${domainUrl}${LAUNCHER_SMB_SALES_CSSFILE_PATH}`;
        document.getElementsByTagName('body')[0].appendChild(genesysScriptCsslink);
      }
    } else return null;
  } catch (error) {
    console.error('Error injecting Genesys assets:', error);
    return null;
  }
};

const getScripts = async () => {
  try {
    let response = getCachedWebMessengerConfig();

    if (!response) {
      response = await getWebMessengerConfig();
      setCachedWebMessengerConfig(response, getCacheMinutes(response), BRAND);
    }

    const config = response?.webMessengerConfig?.config;
    const holidays = response?.webMessengerConfig?.holidays;
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const date = now.getDate();
    const hours = now.getUTCHours();
    const minutes = now.getUTCMinutes();
    const seconds = now.getUTCSeconds();
    const day = now.getUTCDay();
    const currDate = `${month}/${date}/${year}`;
    const dstStartDate = dayLightSavingsData[year].start_date;
    const dstEndDate = dayLightSavingsData[year].end_date;
    let scriptList = null;
    let filteredConfig = null;
    let schedule = null;
    let currTime = null;

    // // If DST is in effect, add 1 hour to currentTime
    // if (isDSTCheck(dstStartDate, dstEndDate, currDate)) {
    //   // If currentTime is 24th hour, don't add 1 instead, make it to 1
    //   currTime =
    //     hours === 24
    //       ? (currTime = `${1}:${minutes}:${seconds}`)
    //       : (currTime = `${hours + 1}:${minutes}:${seconds}`);
    // } else {
    //   currTime = `${hours}:${minutes}:${seconds}`;
    // }
    // If DST is in effect, add 1 hour to currentTime safely
    const pad = n => String(n).padStart(2, '0');
    if (isDSTCheck(dstStartDate, dstEndDate, currDate)) {
      const adjustedHours = (hours + 1) % 24;
      currTime = `${pad(adjustedHours)}:${pad(minutes)}:${pad(seconds)}`;
    } else {
      currTime = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }

    const pathName = getPathName();

    // Filter the config, which matches current page pathName
    config?.forEach(object => {
      const routes = object?.PAGES?.map(p => p.PAGE);
      if (routes?.includes(pathName)) {
        filteredConfig = object;
      }
    });

    // For filtered config, check if all startTime and endTime is null in schedule array
    const areDatesNull = filteredConfig?.SCHEDULES?.every(
      s => s.START_TIME === null && s.END_TIME === null,
    );

    // If all startTime and endTime are null, than insert the script 24/7
    if (areDatesNull) {
      // insert and early return
      scriptList = filteredConfig?.SCRIPTS?.map(s => s.SCRIPT);
    } else {
      // If all startTime and endTime are not null, select the schedule type depending on current day i.e.(WORKDAY, WEEKEND, HOLIDAY)
      const holidaysList = holidays.map(holiday => holiday.DATE);

      // Added 1 to now.getMonth(), as index starts from 0 (jan-0, feb-1,...,dec-11)
      if (holidaysList.includes(getCurrentDate())) {
        console.log(
          'Holiday schedule',
          filteredConfig?.SCHEDULES.filter(e => e.TYPE === 'HOLIDAY'),
        );
        schedule = filteredConfig?.SCHEDULES.filter(e => e.TYPE === 'HOLIDAY');
      } else if (day === 0 || day === 6) {
        console.log(
          'Weekend schedule',
          filteredConfig?.SCHEDULES.filter(e => e.TYPE === 'WEEKEND'),
        );
        schedule = filteredConfig?.SCHEDULES.filter(e => e.TYPE === 'WEEKEND');
      } else {
        console.log(
          'Workday schedule',
          filteredConfig?.SCHEDULES.filter(e => e.TYPE === 'WORKDAY'),
        );
        schedule = filteredConfig?.SCHEDULES.filter(e => e.TYPE === 'WORKDAY');
      }

      if (schedule?.length) {
        const [firstElement] = schedule;
        schedule = firstElement;
      } else {
        console.log('Failed to select schedule!!!');
      }

      if (
        timeToSeconds(currTime) > timeToSeconds(schedule?.START_TIME ?? '00:00:00') &&
        timeToSeconds(currTime) < timeToSeconds(schedule?.END_TIME ?? '23:59:59')
      ) {
        scriptList = filteredConfig?.SCRIPTS?.map(s => s.SCRIPT);
        console.log(
          'Fell in scheduled time: scripts injected',
          'start time:',
          schedule?.START_TIME,
          'end time:',
          schedule?.END_TIME,
          'current time:',
          currTime,
        );
      } else {
        console.log(
          'Did not fall in scheduled time',
          schedule?.START_TIME,
          schedule?.END_TIME,
          'current time',
          currTime,
        );
      }
    }

    // Inject the scripts into DOM
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => injectGenesysAssets(scriptList));
    } else {
      // If DOM is already ready
      injectGenesysAssets(scriptList);
    }
  } catch (err) {
    console.error(err.message);
  }
};
getScripts();
